import { useDebounceState } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  Dialog,
  DialogContent,
  ErrorBoundary,
  FormSearchInput,
  ScrollArea,
  Text,
  theme
} from "@prismicio/editor-ui"
import { Suspense, useTransition } from "react"

import { useEditorConfig } from "../../EditorConfig"
import {
  type IntegrationItem,
  useIntegrationCatalogSearch
} from "./integrationData"
import {
  IntegrationPreviewCard,
  IntegrationPreviewCardSkeleton
} from "./IntegrationPreviewCard"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalogId: string
  onSelectItem: (item: IntegrationItem) => void
}

export function IntegrationDialog(props: Props) {
  const { open, onOpenChange, catalogId, onSelectItem } = props

  const searchTerm = useDebounceState({
    initialState: ""
  })

  const config = useEditorConfig()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      hasClose={false}
      size={{ width: 612, height: 724 }}
    >
      <DialogContent>
        <Box flexDirection="column" height="100%">
          <Box padding={{ top: 16, inline: 16 }} flexDirection="column" gap={8}>
            <FormSearchInput
              value={searchTerm.value}
              onValueChange={searchTerm.set}
              placeholder="Search in your catalog"
            />
            <Text variant="smallBold" color="grey11">
              Select an item
            </Text>
          </Box>
          {config.integrationFields ? (
            <ErrorBoundary
              renderError={() => (
                <EmptyList message="There was an error while fetching your catalog items." />
              )}
            >
              <Suspense
                fallback={
                  <Box
                    flexDirection="column"
                    gap={12}
                    padding={{ top: 10, inline: 16, bottom: 16 }}
                  >
                    <IntegrationPreviewCardSkeleton />
                    <IntegrationPreviewCardSkeleton />
                    <IntegrationPreviewCardSkeleton />
                    <IntegrationPreviewCardSkeleton />
                    <IntegrationPreviewCardSkeleton />
                  </Box>
                }
              >
                <IntegrationItems
                  searchTerm={searchTerm.debouncedValue}
                  catalogId={catalogId}
                  onItemClick={onSelectItem}
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <EmptyList message="Slice Machine does not yet support mock data for integration field in the simulator." />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

interface IntegrationFieldItemsProps {
  catalogId: string
  searchTerm: string
  onItemClick: (item: IntegrationItem) => void
}

function IntegrationItems(props: IntegrationFieldItemsProps) {
  const { catalogId, searchTerm, onItemClick } = props

  const [isLoadingNextPage, startTransition] = useTransition()

  const { items, hasNextPage, fetchNextPage } = useIntegrationCatalogSearch({
    catalogId,
    searchTerm
  })

  return (
    <>
      <ScrollArea
        onScrollToBottom={() => hasNextPage && startTransition(fetchNextPage)}
        style={{
          flexGrow: 1,
          paddingTop: theme.space[10],
          paddingBottom: theme.space[16],
          paddingInline: theme.space[16],
          display: "flex",
          flexDirection: "column",
          gap: theme.space[12]
        }}
        threshold={252} // ~2 items
      >
        {items.length === 0 ? (
          <EmptyList />
        ) : (
          items.map((item) => (
            <IntegrationPreviewCard
              key={item.id}
              item={item}
              catalogId={catalogId}
              onClick={() => onItemClick(item)}
            />
          ))
        )}

        <AnimatedElement animation={{ name: "fade" }}>
          {isLoadingNextPage && (
            <Text color="grey11" align="center" sx={{ marginBlock: 8 }}>
              Loading more results ...
            </Text>
          )}
        </AnimatedElement>
      </ScrollArea>
    </>
  )
}

interface EmptyListProps {
  message?: string
}

function EmptyList(props: EmptyListProps) {
  const { message = "We can't find any items matching your search" } = props

  const title = "Oops!"

  return (
    <Box justifyContent="center" flexDirection="column" height="100%">
      <BlankSlate>
        <BlankSlateIcon
          name="close"
          lineColor="purple10"
          backgroundColor="purple3"
          borderColor="purple6"
          borderStyle="dashed"
          size="xlarge"
        />
        <BlankSlateTitle size="big">{title}</BlankSlateTitle>
        <BlankSlateDescription width={360}>{message}</BlankSlateDescription>
      </BlankSlate>
    </Box>
  )
}
